// Browser-side NKF-AEC using onnxruntime-web for the KGNet sub-network.
//
// Mirrors the AecEngine interface so it drops into the existing
// sync-aec-worklet → main-thread pipeline:
//
//   const engine = new NkfOnnxEngine();
//   await engine.init({ farDelaySamples: 1245 });   // = 77.8 ms @ 16k
//   const cleaned = await engine.processFrame(micI16, farI16);   // 256-sample hops
//
// The ONNX model holds only the learned KGNet (5,302 params, 27 KB).  The
// Kalman update and STFT/iSTFT are done in JS for full per-frame control.

const N_FFT      = 1024;
const HOP        = 256;
const FREQ_BINS  = N_FFT / 2 + 1;       // 513
const L_TAPS     = 4;                   // NKF L=4
const RNN_DIM    = 18;
const INPUT_DIM  = 2 * L_TAPS + 1;      // = 9

const ORT_VERSION = '1.18.0';
const ORT_URL     = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${ORT_VERSION}/dist/ort.min.js`;
const ORT_WASM    = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${ORT_VERSION}/dist/`;

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = () => reject(new Error('failed to load ' + src));
        document.head.appendChild(s);
    });
}

// ─── Length-1024 radix-2 FFT (real input) ──────────────────────────────────
const _bitrev = new Uint16Array(N_FFT);
{
    let j = 0;
    for (let i = 0; i < N_FFT; i++) {
        _bitrev[i] = j;
        let bit = N_FFT >> 1;
        while (bit && (j & bit)) { j ^= bit; bit >>= 1; }
        j ^= bit;
    }
}
const _cosT = new Float32Array(N_FFT / 2);
const _sinT = new Float32Array(N_FFT / 2);
for (let i = 0; i < N_FFT / 2; i++) {
    _cosT[i] = Math.cos(-2 * Math.PI * i / N_FFT);
    _sinT[i] = Math.sin(-2 * Math.PI * i / N_FFT);
}

function fftReal(realIn, outRe, outIm) {
    for (let i = 0; i < N_FFT; i++) {
        outRe[_bitrev[i]] = realIn[i];
        outIm[_bitrev[i]] = 0;
    }
    for (let size = 2; size <= N_FFT; size <<= 1) {
        const half = size >> 1;
        const tStep = N_FFT / size;
        for (let i = 0; i < N_FFT; i += size) {
            for (let k = 0; k < half; k++) {
                const c = _cosT[k * tStep];
                const s = _sinT[k * tStep];
                const xRe = outRe[i + k + half];
                const xIm = outIm[i + k + half];
                const tRe = c * xRe - s * xIm;
                const tIm = s * xRe + c * xIm;
                outRe[i + k + half] = outRe[i + k] - tRe;
                outIm[i + k + half] = outIm[i + k] - tIm;
                outRe[i + k] += tRe;
                outIm[i + k] += tIm;
            }
        }
    }
}

const _ifftRe = new Float32Array(N_FFT);
const _ifftIm = new Float32Array(N_FFT);
function ifftReal(re, im, outReal) {
    for (let i = 0; i < N_FFT; i++) {
        _ifftRe[_bitrev[i]] = re[i];
        _ifftIm[_bitrev[i]] = -im[i];
    }
    for (let size = 2; size <= N_FFT; size <<= 1) {
        const half = size >> 1;
        const tStep = N_FFT / size;
        for (let i = 0; i < N_FFT; i += size) {
            for (let k = 0; k < half; k++) {
                const c = _cosT[k * tStep];
                const s = _sinT[k * tStep];
                const xRe = _ifftRe[i + k + half];
                const xIm = _ifftIm[i + k + half];
                const tRe = c * xRe - s * xIm;
                const tIm = s * xRe + c * xIm;
                _ifftRe[i + k + half] = _ifftRe[i + k] - tRe;
                _ifftIm[i + k + half] = _ifftIm[i + k] - tIm;
                _ifftRe[i + k] += tRe;
                _ifftIm[i + k] += tIm;
            }
        }
    }
    const inv = 1 / N_FFT;
    for (let i = 0; i < N_FFT; i++) outReal[i] = _ifftRe[i] * inv;
}

// Hann (periodic) window + steady-state OLA normalizer (= 1.5 for hann + hop=N/4).
const _WIN = new Float32Array(N_FFT);
for (let i = 0; i < N_FFT; i++) {
    _WIN[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / N_FFT);
}
const _OLA_NORM = (() => {
    const nFr = 4 * (N_FFT / HOP);
    const acc = new Float64Array((nFr - 1) * HOP + N_FFT);
    for (let k = 0; k < nFr; k++)
        for (let i = 0; i < N_FFT; i++) acc[k * HOP + i] += _WIN[i] * _WIN[i];
    return acc[N_FFT];                  // steady-state position
})();

export class NkfOnnxEngine {
    constructor() {
        this.frameSize = HOP;            // 256 samples = 16 ms @ 16 kHz
        this.farDelaySamples = 0;
        this.session = null;
    }

    /**
     * @param {object} opts
     * @param {string} [opts.modelUrl]          path to nkf_kgnet.onnx
     * @param {number} [opts.farDelaySamples]   round-trip delay measured by calibration
     */
    async init({ modelUrl = '/static/nkf_kgnet.onnx', farDelaySamples = 0 } = {}) {
        await loadScript(ORT_URL);
        const ort = window.ort;
        if (!ort) throw new Error('onnxruntime-web not available');
        ort.env.wasm.wasmPaths = ORT_WASM;
        this._ort = ort;

        console.log(`[nkf] loading ${modelUrl}…`);
        this.session = await ort.InferenceSession.create(modelUrl, {
            executionProviders: ['wasm'],
        });

        // ── persistent state across frames ──────────────────────────────────
        // KGNet GRU hidden states (4 of them, each [1, F, RNN_DIM]).
        const stateLen = 1 * FREQ_BINS * RNN_DIM;
        this._h_rr = new Float32Array(stateLen);
        this._h_ir = new Float32Array(stateLen);
        this._h_ri = new Float32Array(stateLen);
        this._h_ii = new Float32Array(stateLen);

        // Per-bin Kalman state — flat (F, L) layout.
        const klLen = FREQ_BINS * L_TAPS;
        this._h_prior_re = new Float32Array(klLen);
        this._h_prior_im = new Float32Array(klLen);
        this._h_post_re  = new Float32Array(klLen);
        this._h_post_im  = new Float32Array(klLen);

        // x-history: last L-1 frames of far_freq, per-bin.  Layout
        // hist[t * F + f], t ∈ [0, L-2].  Newest is at t=L-2.
        const histLen = (L_TAPS - 1) * FREQ_BINS;
        this._x_hist_re = new Float32Array(histLen);
        this._x_hist_im = new Float32Array(histLen);

        // STFT rolling analysis windows + iSTFT OLA buffer.
        this._in_buf  = new Float32Array(N_FFT);
        this._lpb_buf = new Float32Array(N_FFT);
        this._out_buf = new Float32Array(N_FFT);

        // FFT scratch.
        this._win_in_scratch  = new Float32Array(N_FFT);
        this._win_lpb_scratch = new Float32Array(N_FFT);
        this._fft_re = new Float32Array(N_FFT);
        this._fft_im = new Float32Array(N_FFT);
        this._lpb_re = new Float32Array(N_FFT);
        this._lpb_im = new Float32Array(N_FFT);
        this._est_t  = new Float32Array(N_FFT);

        // KGNet input buffers (pre-allocated for reuse).
        this._in_real = new Float32Array(FREQ_BINS * INPUT_DIM);
        this._in_imag = new Float32Array(FREQ_BINS * INPUT_DIM);

        // Far-end delay ring buffer.
        this.farDelaySamples = Math.max(0, farDelaySamples | 0);
        this._farDelayBuf = new Float32Array(this.farDelaySamples);

        console.log('[nkf] ready  far_delay_samples=' + this.farDelaySamples +
                    ` (${(this.farDelaySamples * 1000 / 16000).toFixed(1)} ms)`);
    }

    /** Update the round-trip delay (e.g. after running calibration). */
    setFarDelaySamples(samples) {
        const n = Math.max(0, samples | 0);
        if (n === this.farDelaySamples) return;
        this._farDelayBuf = new Float32Array(n);
        this.farDelaySamples = n;
        console.log('[nkf] far_delay_samples updated → ' + n);
    }

    /**
     * Process one HOP-sample (= 16 ms) Int16 frame of (mic, far).
     * Returns Int16Array of length HOP with the cleaned mic.
     * Algorithmic delay: N_FFT - HOP = 768 samples (≈ 48 ms warmup).
     */
    async processFrame(micI16, farI16) {
        // ── 1. apply round-trip delay buffer to far ─────────────────────────
        let farForFft = new Float32Array(HOP);
        if (this.farDelaySamples >= HOP) {
            // delayed_far = oldest HOP samples in the buffer, then shift.
            for (let i = 0; i < HOP; i++) farForFft[i] = this._farDelayBuf[i] / 32768.0;
            this._farDelayBuf.copyWithin(0, HOP);
            for (let i = 0; i < HOP; i++)
                this._farDelayBuf[this.farDelaySamples - HOP + i] = farI16[i];
        } else if (this.farDelaySamples > 0) {
            // delay shorter than one frame — uncommon; treat as effectively 0.
            for (let i = 0; i < HOP; i++) farForFft[i] = farI16[i] / 32768.0;
        } else {
            for (let i = 0; i < HOP; i++) farForFft[i] = farI16[i] / 32768.0;
        }

        // ── 2. slide rolling windows by HOP, copy in new mic and far ───────
        this._in_buf.copyWithin(0, HOP);
        this._lpb_buf.copyWithin(0, HOP);
        for (let i = 0; i < HOP; i++) {
            this._in_buf[N_FFT - HOP + i]  = micI16[i] / 32768.0;
            this._lpb_buf[N_FFT - HOP + i] = farForFft[i];
        }

        // Apply analysis window into pre-allocated scratch buffers, then FFT.
        // (matches torch.stft default behavior for hann + n_fft=win_length)
        for (let i = 0; i < N_FFT; i++) {
            this._win_in_scratch[i]  = this._in_buf[i]  * _WIN[i];
            this._win_lpb_scratch[i] = this._lpb_buf[i] * _WIN[i];
        }
        fftReal(this._win_in_scratch,  this._fft_re, this._fft_im);
        fftReal(this._win_lpb_scratch, this._lpb_re, this._lpb_im);

        // y_freq = mic FFT, x_freq_new = far FFT (only first FREQ_BINS bins matter).

        // ── 3. gather xt (per-bin L taps) into the KGNet input buffer ──────
        // xt[f, 0..L-2] from x_hist, xt[f, L-1] from new x_freq (lpb_*).
        // Also accumulate Σ|xt| so we can match the upstream `continue` skip.
        const inR = this._in_real;
        const inI = this._in_imag;
        const histStride = FREQ_BINS;
        let abs_sum = 0.0;
        for (let f = 0; f < FREQ_BINS; f++) {
            const fb = f * INPUT_DIM;
            const xt0_re = this._x_hist_re[0 * histStride + f];
            const xt0_im = this._x_hist_im[0 * histStride + f];
            const xt1_re = this._x_hist_re[1 * histStride + f];
            const xt1_im = this._x_hist_im[1 * histStride + f];
            const xt2_re = this._x_hist_re[2 * histStride + f];
            const xt2_im = this._x_hist_im[2 * histStride + f];
            const xt3_re = this._lpb_re[f];
            const xt3_im = this._lpb_im[f];
            inR[fb + 0] = xt0_re; inI[fb + 0] = xt0_im;
            inR[fb + 1] = xt1_re; inI[fb + 1] = xt1_im;
            inR[fb + 2] = xt2_re; inI[fb + 2] = xt2_im;
            inR[fb + 3] = xt3_re; inI[fb + 3] = xt3_im;
            abs_sum += Math.sqrt(xt0_re*xt0_re + xt0_im*xt0_im)
                     + Math.sqrt(xt1_re*xt1_re + xt1_im*xt1_im)
                     + Math.sqrt(xt2_re*xt2_re + xt2_im*xt2_im)
                     + Math.sqrt(xt3_re*xt3_re + xt3_im*xt3_im);
        }
        const abs_mean = abs_sum / (FREQ_BINS * L_TAPS);
        const skip_kalman = abs_mean < 1e-5;

        // ── 4. branch on skip, matching the upstream `continue` ────────────
        const cleaned_re = this._fft_re;     // alias; overwritten below
        const cleaned_im = this._fft_im;
        if (skip_kalman) {
            // No state changes, no KGNet call.  echo_hat = 0  →  cleaned = y.
            // (cleaned_re/im already hold the mic spectrum.)
        } else {
            // dh = h_post - h_prior  (per bin, per tap)
            // h_prior <- h_post     (alias swap)
            // e = y - xt · h_prior  (= y - xt · old_h_post)
            // input_feature[f, :] = [xt(L) | e(1) | dh(L)]
            for (let f = 0; f < FREQ_BINS; f++) {
                const fb = f * INPUT_DIM;
                const klBase = f * L_TAPS;
                const xt0_re = inR[fb + 0], xt0_im = inI[fb + 0];
                const xt1_re = inR[fb + 1], xt1_im = inI[fb + 1];
                const xt2_re = inR[fb + 2], xt2_im = inI[fb + 2];
                const xt3_re = inR[fb + 3], xt3_im = inI[fb + 3];

                const op0_re = this._h_post_re[klBase + 0], op0_im = this._h_post_im[klBase + 0];
                const op1_re = this._h_post_re[klBase + 1], op1_im = this._h_post_im[klBase + 1];
                const op2_re = this._h_post_re[klBase + 2], op2_im = this._h_post_im[klBase + 2];
                const op3_re = this._h_post_re[klBase + 3], op3_im = this._h_post_im[klBase + 3];
                const pp0_re = this._h_prior_re[klBase + 0], pp0_im = this._h_prior_im[klBase + 0];
                const pp1_re = this._h_prior_re[klBase + 1], pp1_im = this._h_prior_im[klBase + 1];
                const pp2_re = this._h_prior_re[klBase + 2], pp2_im = this._h_prior_im[klBase + 2];
                const pp3_re = this._h_prior_re[klBase + 3], pp3_im = this._h_prior_im[klBase + 3];

                const dh0_re = op0_re - pp0_re, dh0_im = op0_im - pp0_im;
                const dh1_re = op1_re - pp1_re, dh1_im = op1_im - pp1_im;
                const dh2_re = op2_re - pp2_re, dh2_im = op2_im - pp2_im;
                const dh3_re = op3_re - pp3_re, dh3_im = op3_im - pp3_im;

                // alias swap: h_prior ← h_post (= old op*).
                this._h_prior_re[klBase + 0] = op0_re; this._h_prior_im[klBase + 0] = op0_im;
                this._h_prior_re[klBase + 1] = op1_re; this._h_prior_im[klBase + 1] = op1_im;
                this._h_prior_re[klBase + 2] = op2_re; this._h_prior_im[klBase + 2] = op2_im;
                this._h_prior_re[klBase + 3] = op3_re; this._h_prior_im[klBase + 3] = op3_im;

                // e = y - xt · h_prior   where h_prior is now the OLD h_post.
                const y_re = cleaned_re[f];
                const y_im = cleaned_im[f];
                const dot_re = (xt0_re*op0_re - xt0_im*op0_im)
                             + (xt1_re*op1_re - xt1_im*op1_im)
                             + (xt2_re*op2_re - xt2_im*op2_im)
                             + (xt3_re*op3_re - xt3_im*op3_im);
                const dot_im = (xt0_re*op0_im + xt0_im*op0_re)
                             + (xt1_re*op1_im + xt1_im*op1_re)
                             + (xt2_re*op2_im + xt2_im*op2_re)
                             + (xt3_re*op3_im + xt3_im*op3_re);
                const e_re = y_re - dot_re;
                const e_im = y_im - dot_im;

                inR[fb + 4] = e_re;   inI[fb + 4] = e_im;
                inR[fb + 5] = dh0_re; inI[fb + 5] = dh0_im;
                inR[fb + 6] = dh1_re; inI[fb + 6] = dh1_im;
                inR[fb + 7] = dh2_re; inI[fb + 7] = dh2_im;
                inR[fb + 8] = dh3_re; inI[fb + 8] = dh3_im;
            }

            // ── 5. KGNet ONNX call ─────────────────────────────────────────
            const ort = this._ort;
            const out = await this.session.run({
                in_real: new ort.Tensor('float32', inR, [FREQ_BINS, INPUT_DIM]),
                in_imag: new ort.Tensor('float32', inI, [FREQ_BINS, INPUT_DIM]),
                h_rr:    new ort.Tensor('float32', this._h_rr, [1, FREQ_BINS, RNN_DIM]),
                h_ir:    new ort.Tensor('float32', this._h_ir, [1, FREQ_BINS, RNN_DIM]),
                h_ri:    new ort.Tensor('float32', this._h_ri, [1, FREQ_BINS, RNN_DIM]),
                h_ii:    new ort.Tensor('float32', this._h_ii, [1, FREQ_BINS, RNN_DIM]),
            });
            const kg_re_arr = out.kg_real.data;
            const kg_im_arr = out.kg_imag.data;
            this._h_rr.set(out.h_rr_new.data);
            this._h_ir.set(out.h_ir_new.data);
            this._h_ri.set(out.h_ri_new.data);
            this._h_ii.set(out.h_ii_new.data);

            // ── 6. h_post = h_prior + kg·e,  echo_hat = xt · h_post ─────────
            // cleaned[f] = y - echo_hat
            for (let f = 0; f < FREQ_BINS; f++) {
                const fb = f * INPUT_DIM;
                const klBase = f * L_TAPS;
                const xt0_re = inR[fb + 0], xt0_im = inI[fb + 0];
                const xt1_re = inR[fb + 1], xt1_im = inI[fb + 1];
                const xt2_re = inR[fb + 2], xt2_im = inI[fb + 2];
                const xt3_re = inR[fb + 3], xt3_im = inI[fb + 3];
                const e_re = inR[fb + 4],   e_im = inI[fb + 4];

                const kgB = f * L_TAPS;
                const kg0_re = kg_re_arr[kgB + 0], kg0_im = kg_im_arr[kgB + 0];
                const kg1_re = kg_re_arr[kgB + 1], kg1_im = kg_im_arr[kgB + 1];
                const kg2_re = kg_re_arr[kgB + 2], kg2_im = kg_im_arr[kgB + 2];
                const kg3_re = kg_re_arr[kgB + 3], kg3_im = kg_im_arr[kgB + 3];

                const inc0_re = kg0_re*e_re - kg0_im*e_im;
                const inc0_im = kg0_re*e_im + kg0_im*e_re;
                const inc1_re = kg1_re*e_re - kg1_im*e_im;
                const inc1_im = kg1_re*e_im + kg1_im*e_re;
                const inc2_re = kg2_re*e_re - kg2_im*e_im;
                const inc2_im = kg2_re*e_im + kg2_im*e_re;
                const inc3_re = kg3_re*e_re - kg3_im*e_im;
                const inc3_im = kg3_re*e_im + kg3_im*e_re;

                const hp0_re = this._h_prior_re[klBase + 0] + inc0_re;
                const hp0_im = this._h_prior_im[klBase + 0] + inc0_im;
                const hp1_re = this._h_prior_re[klBase + 1] + inc1_re;
                const hp1_im = this._h_prior_im[klBase + 1] + inc1_im;
                const hp2_re = this._h_prior_re[klBase + 2] + inc2_re;
                const hp2_im = this._h_prior_im[klBase + 2] + inc2_im;
                const hp3_re = this._h_prior_re[klBase + 3] + inc3_re;
                const hp3_im = this._h_prior_im[klBase + 3] + inc3_im;
                this._h_post_re[klBase + 0] = hp0_re; this._h_post_im[klBase + 0] = hp0_im;
                this._h_post_re[klBase + 1] = hp1_re; this._h_post_im[klBase + 1] = hp1_im;
                this._h_post_re[klBase + 2] = hp2_re; this._h_post_im[klBase + 2] = hp2_im;
                this._h_post_re[klBase + 3] = hp3_re; this._h_post_im[klBase + 3] = hp3_im;

                const eh_re = (xt0_re*hp0_re - xt0_im*hp0_im)
                            + (xt1_re*hp1_re - xt1_im*hp1_im)
                            + (xt2_re*hp2_re - xt2_im*hp2_im)
                            + (xt3_re*hp3_re - xt3_im*hp3_im);
                const eh_im = (xt0_re*hp0_im + xt0_im*hp0_re)
                            + (xt1_re*hp1_im + xt1_im*hp1_re)
                            + (xt2_re*hp2_im + xt2_im*hp2_re)
                            + (xt3_re*hp3_im + xt3_im*hp3_re);
                cleaned_re[f] -= eh_re;
                cleaned_im[f] -= eh_im;
            }
        }

        // ── 7. mirror conjugate + iSTFT + OLA ──────────────────────────────
        for (let i = 1; i < FREQ_BINS - 1; i++) {
            cleaned_re[N_FFT - i] =  cleaned_re[i];
            cleaned_im[N_FFT - i] = -cleaned_im[i];
        }
        ifftReal(cleaned_re, cleaned_im, this._est_t);

        // synthesis window + OLA into out_buf (shift by HOP, accumulate).
        for (let i = 0; i < N_FFT; i++) this._est_t[i] *= _WIN[i];
        this._out_buf.copyWithin(0, HOP);
        for (let i = N_FFT - HOP; i < N_FFT; i++) this._out_buf[i] = 0;
        for (let i = 0; i < N_FFT; i++) this._out_buf[i] += this._est_t[i];

        // ── 8. emit first HOP samples (normalized), update x history ───────
        const result = new Int16Array(HOP);
        const inv = 1.0 / _OLA_NORM;
        for (let i = 0; i < HOP; i++) {
            const s = this._out_buf[i] * inv;
            const c = s < -1 ? -1 : s > 1 ? 1 : s;
            result[i] = (c * 32767) | 0;
        }
        // shift x_hist by 1 frame, append new x_freq (= lpb_re/im) at the end.
        // Layout: hist[t*F + f]; we want new layout to drop t=0 and add the new frame as t=L-2.
        // shift: hist[0..F-1*(L-2)] = hist[F..F*(L-1)]
        this._x_hist_re.copyWithin(0, FREQ_BINS, FREQ_BINS * (L_TAPS - 1));
        this._x_hist_im.copyWithin(0, FREQ_BINS, FREQ_BINS * (L_TAPS - 1));
        // append new frame to the end (t = L-2)
        const tailOff = FREQ_BINS * (L_TAPS - 2);
        for (let f = 0; f < FREQ_BINS; f++) {
            this._x_hist_re[tailOff + f] = this._lpb_re[f];
            this._x_hist_im[tailOff + f] = this._lpb_im[f];
        }
        return result;
    }

    destroy() {
        this.session = null;
    }
}
